import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  DocumentTextIcon,
  LanguageIcon,
  MicrophoneIcon,
  PauseIcon,
  PlayIcon,
  StopCircleIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import { transcriptionAPI } from '../../api/transcriptionApi';

const LANGUAGES = [
  { code: '', label: 'Otomatik algıla' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'İngilizce' },
  { code: 'de', label: 'Almanca' },
  { code: 'ru', label: 'Rusça' },
  { code: 'ar', label: 'Arapça' },
  { code: 'fr', label: 'Fransızca' },
];

const TRANSLATE_TARGETS = ['İngilizce', 'Almanca', 'Rusça', 'Arapça', 'Fransızca', 'Türkçe'];
const SPEAKER_CODES = ['A', 'B', 'C', 'D', 'E', 'F'];
const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;
const MAX_UPLOAD_MB = MAX_UPLOAD_BYTES / (1024 * 1024);
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 400;
const EMPTY_SPEAKER_REFS = () => ([
  { name: '', file: null },
  { name: '', file: null },
  { name: '', file: null },
]);

const fmtTime = (s) => {
  if (s == null) return '';
  const total = Math.floor(s);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const speakerLabel = (code, names) => {
  if (!code) return '';
  return names?.[code] || `Konuşmacı ${code}`;
};

const statusBadgeClass = (status) => {
  if (status === 'completed') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  }
  if (status === 'failed') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  }
  return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
};

const statusLabel = (status) => {
  if (status === 'completed') return 'Tamamlandı';
  if (status === 'failed') return 'Başarısız';
  return 'İşleniyor';
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function waitForTranscriptCompletion(id, { onTick } = {}) {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
    const detail = await transcriptionAPI.get(id);
    if (detail.status === 'completed') {
      return { success: true, ...detail };
    }
    if (detail.status === 'failed') {
      throw new Error(detail.error_message || 'Deşifre başarısız oldu.');
    }
    if (onTick) await onTick(detail, attempt);
    await sleep(POLL_INTERVAL_MS);
  }
  throw new Error('Deşifre zaman aşımına uğradı. Kayıtlarım listesinden durumu kontrol edin.');
}

const TranscriptionPage = () => {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('');
  const [error, setError] = useState(null);

  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState('tr');
  const [speakerRefs, setSpeakerRefs] = useState(EMPTY_SPEAKER_REFS);
  const [showSpeakerRefs, setShowSpeakerRefs] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  // Canlı kayıt
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const stopTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => () => stopTracks(), []);

  const startRecording = async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Tarayıcınız kayıt özelliğini desteklemiyor ya da site güvenli (HTTPS) bağlantıda değil. '
        + 'Canlı kayıt için sayfanın https:// veya localhost üzerinden açılması gerekir.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const type = recorder.mimeType || 'audio/webm';
        const outBlob = new Blob(chunksRef.current, { type });
        const ext = type.includes('mp4') ? 'm4a' : 'webm';
        if (outBlob.size > MAX_UPLOAD_BYTES) {
          setError(`Kayıt ${MAX_UPLOAD_MB} MB sınırını aştı. Lütfen daha kısa kayıt yapın.`);
          return;
        }
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        const recFile = new File([outBlob], `kayit-${stamp}.${ext}`, { type: outBlob.type });
        setFile(recFile);
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setRecordSeconds(0);
      timerRef.current = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    } catch (e) {
      setError(e?.name === 'NotAllowedError'
        ? 'Mikrofon izni verilmedi. Tarayıcı ayarlarından mikrofona izin verin.'
        : (e.message || 'Mikrofon başlatılamadı.'));
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    stopTracks();
    setRecording(false);
  };

  const loadList = async () => {
    setLoadingList(true);
    try {
      setList(await transcriptionAPI.list());
    } catch (e) {
      setError(e.message || 'Liste yüklenemedi.');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadList(); }, []);

  const selectFile = (f) => {
    setError(null);
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) {
      setError(`Dosya ${MAX_UPLOAD_MB} MB sınırını aşıyor.`);
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setUploadPhase('Dosya yükleniyor…');
    setError(null);
    try {
      const res = await transcriptionAPI.upload(file, {
        language,
        speakerRefs: speakerRefs.filter((r) => r.name && r.file),
      });
      if (!res?.success) {
        setError(res?.error || 'Deşifre başarısız oldu.');
        return;
      }

      setFile(null);
      setSpeakerRefs(EMPTY_SPEAKER_REFS());
      setShowSpeakerRefs(false);
      if (inputRef.current) inputRef.current.value = '';
      await loadList();

      if (res.status === 'processing') {
        setUploadPhase('Metne çevriliyor…');
        const final = await waitForTranscriptCompletion(res.id, {
          onTick: async () => { await loadList(); },
        });
        setSelected(final);
      } else {
        setSelected(res);
      }
    } catch (e) {
      setError(e.message || 'Yükleme sırasında hata oluştu.');
      await loadList();
    } finally {
      setUploading(false);
      setUploadPhase('');
    }
  };

  const openTranscript = async (id) => {
    setError(null);
    try {
      setSelected(await transcriptionAPI.get(id));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.message || 'Transkript açılamadı.');
    }
  };

  const closeDetail = () => setSelected(null);

  const handleDelete = async (id) => {
    if (!window.confirm('Bu transkript silinsin mi?')) return;
    try {
      await transcriptionAPI.remove(id);
      if (selected?.id === id) setSelected(null);
      await loadList();
    } catch (e) {
      setError(e.message || 'Silinemedi.');
    }
  };

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 space-y-4 max-w-7xl mx-auto">
      <div>
        <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">Ses → Metin</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Ses kaydını yükleyin veya kaydedin; metin, özet ve çeviri oluşturun.
        </p>
      </div>

      {error && (
        <div className="flex items-start justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="shrink-0 text-red-500 hover:text-red-700" aria-label="Kapat">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sol: yükleme + liste — mobilde detay açıkken gizlenir */}
        <div className={`space-y-4 ${selected ? 'hidden lg:block' : 'block'}`}>
          <Card className="shadow-sm">
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base font-semibold">Yeni Kayıt</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-3">
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => { e.preventDefault(); setIsDragging(false); selectFile(e.dataTransfer.files?.[0]); }}
                onClick={() => inputRef.current?.click()}
                className={`cursor-pointer rounded-lg border-2 border-dashed p-4 md:p-5 text-center transition-colors ${
                  isDragging
                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 bg-gray-50 dark:bg-gray-900/30'
                }`}
              >
                <input ref={inputRef} type="file" accept="audio/*,.mp3,.m4a,.wav,.webm,.ogg,.mp4"
                  className="hidden" onChange={(e) => selectFile(e.target.files?.[0])} />
                {file ? (
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <DocumentTextIcon className="h-5 w-5 text-gray-500 shrink-0" />
                    <span className="font-medium text-gray-800 dark:text-gray-100 truncate max-w-[220px]">{file.name}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 text-gray-500 dark:text-gray-400">
                    <ArrowUpTrayIcon className="h-6 w-6" />
                    <p className="text-sm font-medium">Dosya seçin veya sürükleyin</p>
                    <p className="text-xs">mp3, m4a, wav, webm — en fazla {MAX_UPLOAD_MB} MB (25 MB üzeri otomatik parçalanır)</p>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
                {recording ? (
                  <>
                    <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 font-medium">
                      <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                      Kayıt · {fmtTime(recordSeconds)}
                    </span>
                    <Button type="button" variant="destructive" size="sm" onClick={stopRecording} className="w-full sm:w-auto">
                      <StopCircleIcon className="h-4 w-4" /> Durdur
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Canlı kayıt</span>
                    <Button type="button" variant="outline" size="sm" onClick={startRecording} disabled={uploading} className="w-full sm:w-auto">
                      <MicrophoneIcon className="h-4 w-4" /> Kayıt Başlat
                    </Button>
                  </>
                )}
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-gray-700 dark:text-gray-300">Dil</Label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2.5 text-sm text-gray-900 dark:text-white"
                >
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Konuşmacı ayrımı için kayıt dilini doğru seçin (Türkçe kayıtta Türkçe).
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowSpeakerRefs((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  Konuşmacı referans sesleri (isteğe bağlı)
                  <span className="text-gray-400">{showSpeakerRefs ? '−' : '+'}</span>
                </button>
                {showSpeakerRefs && (
                  <div className="px-3 pb-3 space-y-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="pt-2 text-[11px] text-gray-500 dark:text-gray-400">
                      Her kişiden 2–10 sn temiz örnek ses yükleyin (en fazla 3 kişi). Ayrım kalitesini artırır.
                    </p>
                    {speakerRefs.map((ref, idx) => (
                      <div key={idx} className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={ref.name}
                          placeholder={`Kişi ${idx + 1} adı`}
                          onChange={(e) => setSpeakerRefs((prev) => prev.map((r, i) => (i === idx ? { ...r, name: e.target.value } : r)))}
                          className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm"
                        />
                        <input
                          type="file"
                          accept="audio/*,.wav,.mp3,.m4a,.webm"
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            setSpeakerRefs((prev) => prev.map((r, i) => (i === idx ? { ...r, file: f } : r)));
                          }}
                          className="flex-1 text-xs text-gray-600 dark:text-gray-300 file:mr-2 file:rounded file:border-0 file:bg-gray-100 file:px-2 file:py-1.5 dark:file:bg-gray-600"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="button"
                onClick={handleUpload}
                disabled={!file || uploading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {uploading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <DocumentTextIcon className="h-4 w-4" />}
                {uploading ? 'İşleniyor…' : 'Metne Çevir'}
              </Button>
              {uploading && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {uploadPhase || 'Kayıt uzunluğuna göre birkaç dakika sürebilir.'}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="p-4 pb-2 border-b border-gray-100 dark:border-gray-700">
              <CardTitle className="text-base font-semibold">Kayıtlarım</CardTitle>
            </CardHeader>
            <div className="max-h-[50vh] lg:max-h-[55vh] overflow-auto divide-y divide-gray-100 dark:divide-gray-700">
              {loadingList ? (
                <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Yükleniyor…</p>
              ) : list.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Henüz kayıt yok.</p>
              ) : list.map((t) => (
                <div
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') openTranscript(t.id); }}
                  className={`flex items-center gap-2 px-3 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 active:bg-gray-100 dark:active:bg-gray-700/60 ${
                    selected?.id === t.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                  onClick={() => openTranscript(t.id)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{t.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {t.duration ? (
                        <span className="text-xs text-gray-500 dark:text-gray-400">{fmtTime(t.duration)}</span>
                      ) : null}
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadgeClass(t.status)}`}>
                        {statusLabel(t.status)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }}
                    className="shrink-0 rounded p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                    title="Sil"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Sağ: detay — mobilde tam genişlik */}
        <div className={`lg:col-span-2 ${!selected ? 'hidden lg:block' : 'block'}`}>
          {selected ? (
            <TranscriptDetail
              key={selected.id}
              initial={selected}
              onChanged={loadList}
              setError={setError}
              onBack={closeDetail}
            />
          ) : (
            <Card className="shadow-sm min-h-[280px] flex items-center justify-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 px-4 text-center">
                Listeden bir kayıt seçin veya yeni ses yükleyin.
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

/** Web Audio API oynatıcı — <audio> etiketi IIS/cross-origin ortamında decode edemeyebilir. */
const TranscriptAudioPlayer = ({ transcriptId, originalFilename }) => {
  const audioCtxRef = useRef(null);
  const bufferRef = useRef(null);
  const sourceRef = useRef(null);
  const startAtRef = useRef(0);
  const offsetRef = useRef(0);
  const rafRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);

  const stopSource = () => {
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch (_) { /* already stopped */ }
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const tick = () => {
    const ctx = audioCtxRef.current;
    const buf = bufferRef.current;
    if (!ctx || !sourceRef.current || !buf) return;
    const dur = buf.duration;
    const t = Math.min(offsetRef.current + (ctx.currentTime - startAtRef.current), dur);
    setCurrentTime(t);
    if (t < dur) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      setPlaying(false);
      offsetRef.current = 0;
      setCurrentTime(0);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    stopSource();
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    bufferRef.current = null;

    (async () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) throw new Error('Tarayıcınız ses oynatmayı desteklemiyor.');

        const raw = await transcriptionAPI.fetchAudioArrayBuffer(transcriptId);
        if (cancelled) return;

        const ctx = new AC();
        let decoded;
        try {
          decoded = await ctx.decodeAudioData(raw.slice(0));
        } catch {
          throw new Error('Ses dosyası okunamadı (bozuk veya desteklenmeyen format).');
        }
        if (cancelled) {
          ctx.close();
          return;
        }

        audioCtxRef.current = ctx;
        bufferRef.current = decoded;
        setDuration(decoded.duration);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Ses yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      stopSource();
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
      bufferRef.current = null;
    };
  }, [transcriptId]);

  const play = async () => {
    const ctx = audioCtxRef.current;
    const buffer = bufferRef.current;
    if (!ctx || !buffer) return;

    if (ctx.state === 'suspended') await ctx.resume();

    stopSource();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.onended = () => {
      if (sourceRef.current === source) {
        setPlaying(false);
        offsetRef.current = 0;
        setCurrentTime(0);
        sourceRef.current = null;
      }
    };

    const offset = offsetRef.current;
    source.start(0, offset);
    startAtRef.current = ctx.currentTime;
    sourceRef.current = source;
    setPlaying(true);
    tick();
  };

  const pause = () => {
    const ctx = audioCtxRef.current;
    if (ctx && sourceRef.current) {
      offsetRef.current = Math.min(
        offsetRef.current + (ctx.currentTime - startAtRef.current),
        duration,
      );
      setCurrentTime(offsetRef.current);
    }
    stopSource();
    setPlaying(false);
  };

  const togglePlay = () => {
    if (playing) pause();
    else play();
  };

  const handleSeek = (e) => {
    const next = Number(e.target.value);
    offsetRef.current = next;
    setCurrentTime(next);
    if (playing) {
      stopSource();
      play();
    }
  };

  if (loading) {
    return <p className="mt-3 text-xs text-gray-400">Ses yükleniyor…</p>;
  }

  if (error) {
    return (
      <div className="mt-3 space-y-1">
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        <button
          type="button"
          onClick={() => transcriptionAPI.downloadAudio(transcriptId, originalFilename || 'kayit.wav')}
          className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Ses dosyasını indir
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-center gap-2 sm:gap-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 px-2 sm:px-3 py-2.5">
      <button
        type="button"
        onClick={togglePlay}
        className="flex-shrink-0 rounded-full p-2 text-gray-700 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700 touch-manipulation"
        title={playing ? 'Duraklat' : 'Oynat'}
        aria-label={playing ? 'Duraklat' : 'Oynat'}
      >
        {playing ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
      </button>
      <span className="flex-shrink-0 text-[10px] sm:text-[11px] tabular-nums text-gray-500 dark:text-gray-400 w-8 sm:w-9">
        {fmtTime(currentTime)}
      </span>
      <input
        type="range"
        min={0}
        max={duration || 1}
        step={0.01}
        value={currentTime}
        onChange={handleSeek}
        className="flex-1 h-2 accent-blue-600 cursor-pointer min-w-0 touch-manipulation"
        aria-label="Oynatma konumu"
      />
      <span className="flex-shrink-0 text-[11px] tabular-nums text-gray-500 dark:text-gray-400 w-9 text-right">
        {fmtTime(duration)}
      </span>
    </div>
  );
};

const TranscriptDetail = ({ initial, onChanged, setError, onBack }) => {
  const [t, setT] = useState(initial);
  const [segments, setSegments] = useState(initial.segments || []);
  const [speakerNames, setSpeakerNames] = useState(initial.speaker_names || {});
  const [title, setTitle] = useState(initial.title || '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null); // 'summary' | 'translate' | 'chat'

  const [translateTarget, setTranslateTarget] = useState('İngilizce');
  const [chatInput, setChatInput] = useState('');
  const [chatLog, setChatLog] = useState([]);

  useEffect(() => {
    setT(initial);
    setSegments(initial.segments || []);
    setSpeakerNames(initial.speaker_names || {});
    setTitle(initial.title || '');
    setDirty(false);
    setChatLog([]);
  }, [initial]);

  const speakers = useMemo(() => {
    const set = new Set();
    (segments || []).forEach((s) => { if (s.speaker) set.add(s.speaker); });
    return Array.from(set);
  }, [segments]);

  const updateSegmentText = (idx, text) => {
    setSegments((prev) => prev.map((s, i) => (i === idx ? { ...s, text } : s)));
    setDirty(true);
  };

  const updateSegmentSpeaker = (idx, speaker) => {
    setSegments((prev) => prev.map((s, i) => (i === idx ? { ...s, speaker } : s)));
    setDirty(true);
  };

  const renameSpeaker = (code, name) => {
    setSpeakerNames((prev) => ({ ...prev, [code]: name }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await transcriptionAPI.update(t.id, { title, segments, speaker_names: speakerNames });
      setT(updated);
      setDirty(false);
      onChanged?.();
    } catch (e) {
      setError?.(e.message || 'Kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleSummarize = async () => {
    setBusy('summary');
    try {
      const res = await transcriptionAPI.summarize(t.id);
      if (res?.success) setT((p) => ({ ...p, summary: res.summary }));
      else setError?.(res?.error || 'Özet üretilemedi.');
    } catch (e) { setError?.(e.message); } finally { setBusy(null); }
  };

  const handleTranslate = async () => {
    setBusy('translate');
    try {
      const res = await transcriptionAPI.translate(t.id, translateTarget);
      if (res?.success) {
        setT((p) => ({ ...p, translations: { ...(p.translations || {}), [translateTarget]: res.translated_text } }));
      } else setError?.(res?.error || 'Çeviri yapılamadı.');
    } catch (e) { setError?.(e.message); } finally { setBusy(null); }
  };

  const handleChat = async () => {
    const q = chatInput.trim();
    if (!q || busy) return;
    setChatInput('');
    setChatLog((p) => [...p, { role: 'user', content: q }]);
    setBusy('chat');
    try {
      const res = await transcriptionAPI.chat(t.id, q, chatLog);
      if (res?.success) setChatLog((p) => [...p, { role: 'assistant', content: res.answer }]);
      else setChatLog((p) => [...p, { role: 'assistant', content: res?.error || 'Yanıt alınamadı.' }]);
    } catch (e) {
      setChatLog((p) => [...p, { role: 'assistant', content: e.message || 'Hata.' }]);
    } finally { setBusy(null); }
  };

  const exportBtn = (fmt, label) => (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => transcriptionAPI.downloadExport(t.id, fmt, `${title || 'transkript'}.${fmt}`)}
      className="text-xs"
    >
      <ArrowDownTrayIcon className="h-3.5 w-3.5" /> {label}
    </Button>
  );

  if (t.status === 'failed') {
    return (
      <Card className="shadow-sm border-red-200 dark:border-red-800">
        <CardContent className="p-4 text-sm text-red-700 dark:text-red-300">
          {onBack && (
            <button type="button" onClick={onBack} className="lg:hidden mb-3 flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
              <ChevronLeftIcon className="h-4 w-4" /> Kayıtlara dön
            </button>
          )}
          Deşifre başarısız oldu: {t.error_message || 'Bilinmeyen hata.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="lg:hidden flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white py-1"
        >
          <ChevronLeftIcon className="h-5 w-5" /> Kayıtlara dön
        </button>
      )}

      <Card className="shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <input
              value={title}
              onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
              className="flex-1 text-base md:text-lg font-semibold bg-transparent border-b border-gray-200 dark:border-gray-600 focus:border-blue-500 outline-none text-gray-900 dark:text-gray-100 py-1 min-w-0"
            />
            <Button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving}
              size="sm"
              className="w-full sm:w-auto shrink-0 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
              Kaydet
            </Button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t.duration ? `${fmtTime(t.duration)} · ` : ''}{segments.length} segment
            {t.transcription_engine ? ` · ${t.transcription_engine}` : ''}
            {typeof t.detected_speaker_count === 'number' ? ` · ${t.detected_speaker_count} konuşmacı` : ''}
          </p>
          {t.transcription_engine === 'whisper-1' && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Konuşmacı ayrımı kapalı (whisper-1). .env dosyasında TRANSCRIBE_OPENAI_MODEL=gpt-4o-transcribe-diarize olduğundan emin olun.
            </p>
          )}
          {t.transcription_engine !== 'whisper-1' && speakers.length <= 1 && segments.length > 2 && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Model tek konuşmacı algıladı. Telefon/hoparlör kayıtlarında sık görülür; segment satırından A/B/C atayabilir veya referans ses ile yeniden yükleyebilirsiniz.
            </p>
          )}
          {t.status === 'completed' && (
            <TranscriptAudioPlayer transcriptId={t.id} originalFilename={t.original_filename} />
          )}

          {speakers.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {speakers.map((code) => (
                <div key={code} className="flex items-center gap-1.5 text-xs w-full sm:w-auto">
                  <span className="text-gray-500 dark:text-gray-400 shrink-0">Konuşmacı {code}:</span>
                  <input
                    value={speakerNames[code] || ''}
                    placeholder={`Konuşmacı ${code}`}
                    onChange={(e) => renameSpeaker(code, e.target.value)}
                    className="flex-1 min-w-[100px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-100 dark:border-gray-700">
            {exportBtn('txt', 'TXT')}
            {exportBtn('docx', 'Word')}
            {exportBtn('srt', 'SRT')}
            {exportBtn('vtt', 'VTT')}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base font-semibold">Transkript</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="space-y-3 max-h-[45vh] md:max-h-[40vh] overflow-auto pr-1">
            {segments.map((s, idx) => (
              <div key={s.id || idx} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <div className="flex sm:flex-col sm:w-28 shrink-0 gap-2 sm:gap-1">
                  <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 sm:pt-0">{fmtTime(s.start)}</div>
                  <select
                    value={s.speaker || ''}
                    onChange={(e) => updateSegmentSpeaker(idx, e.target.value)}
                    className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-xs text-gray-800 dark:text-gray-100 min-h-[36px]"
                    aria-label="Konuşmacı"
                  >
                    <option value="">—</option>
                    {SPEAKER_CODES.map((code) => (
                      <option key={code} value={code}>{speakerLabel(code, speakerNames)}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={s.text}
                  rows={1}
                  onChange={(e) => updateSegmentText(idx, e.target.value)}
                  className="flex-1 resize-none rounded-md border border-gray-200 dark:border-gray-600 focus:border-blue-500 bg-white dark:bg-gray-700 px-2 py-2 text-sm text-gray-800 dark:text-gray-100 outline-none min-h-[2.5rem]"
                  onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = `${e.target.scrollHeight}px`; }}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4 space-y-4">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Özet</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSummarize}
                  disabled={busy === 'summary'}
                  className="w-full sm:w-auto"
                >
                  {busy === 'summary' ? 'Hazırlanıyor…' : 'Özet Oluştur'}
                </Button>
              </div>
              {t.summary ? (
                <p className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-200">{t.summary}</p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">Henüz özet yok.</p>
              )}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <div className="flex flex-col gap-2 mb-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                  <LanguageIcon className="h-4 w-4 text-gray-500" /> Çeviri
                </h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    value={translateTarget}
                    onChange={(e) => setTranslateTarget(e.target.value)}
                    className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm"
                  >
                    {TRANSLATE_TARGETS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleTranslate}
                    disabled={busy === 'translate'}
                    className="w-full sm:w-auto shrink-0"
                  >
                    {busy === 'translate' ? 'Çevriliyor…' : 'Çevir'}
                  </Button>
                </div>
              </div>
              {t.translations?.[translateTarget] ? (
                <p className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-200">{t.translations[translateTarget]}</p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">Bu dilde çeviri yok.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm flex flex-col">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-1.5">
              <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-500" />
              Transkript Sorgusu
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex flex-col flex-1 min-h-[200px]">
            <div className="flex-1 max-h-[28vh] overflow-auto space-y-2 mb-3">
              {chatLog.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">Transkript metni hakkında soru sorabilirsiniz.</p>
              )}
              {chatLog.map((m, i) => (
                <div
                  key={i}
                  className={`text-sm rounded-md px-3 py-2 border ${
                    m.role === 'user'
                      ? 'bg-gray-50 border-gray-200 dark:bg-gray-900/40 dark:border-gray-600 text-gray-800 dark:text-gray-100'
                      : 'bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {m.content}
                </div>
              ))}
              {busy === 'chat' && <p className="text-xs text-gray-500">Yanıtlanıyor…</p>}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleChat(); }}
                placeholder="Sorunuzu yazın…"
                className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2.5 text-sm min-h-[44px]"
              />
              <Button
                type="button"
                onClick={handleChat}
                disabled={!chatInput.trim() || busy === 'chat'}
                className="w-full sm:w-auto shrink-0 bg-blue-600 hover:bg-blue-700 text-white min-h-[44px]"
              >
                Gönder
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TranscriptionPage;
